import React, { useEffect, useState, useRef } from "react";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {formatName, getAgeByYearMonthDay, formatJoinLeaveCompanyDate, formatPadStartNumber} from "utils/format";
import { updateTalent, setEditingTalent, setIsLoadingAddUpdate } from "reducers/companyTalent";
import {useDispatch, useSelector} from "react-redux";
import {LoadingSpinner} from "components/Icons";
import TextInput from "../../../components/TextInput";
import CustomTextarea from "../../../components/CustomTextarea";
import CustomIconButton from "../../../components/CustomIconButton";
import CustomButton from "../../../components/CustomButton";
import CheckboxButton from "../../../components/CheckboxButton";
import { validateFieldReq, validateEmail } from "../../../utils/validation";
import { STAFF_TYPE, TALENT_TEMP_STATUS } from "../../../constants/index";
import FileItem from "../../../components/FileItem";
import { downloadFileAttachment } from "utils/util";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import CustomToast from "../../../components/CustomToast";
import i18n from 'i18next';

const initialData = {
  first_name: "",
  last_name: "",
  email: "",
  talent_type: null,
  referral_hiring: "",
  note: "",
  identity_verification_label: "",
  identity_verification_text: ""
};

interface ModalProps {
  onClose: () => void;
}

const TalentRegistrationPanel: React.FC<ModalProps> = ({ onClose }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [formData, setFormData] = useState(initialData);
  const lastNameRef = useRef<HTMLFormElement>(null);
  const emailRef = useRef<HTMLFormElement>(null);
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { selectedTalent, talentOptionsRelatedData, isEditing, isLoadingAddUpdate } = useSelector(
    (state: any) => state.companyTalent,
  );
  const [errors, setErrors] = useState({ first_name: "", last_name: "", email: "", type: "", identity_verification_label: "", identity_verification_text: "" });
  const [focusField, setFocusField] = useState("");

  const talentTypeOptions = talentOptionsRelatedData?.talent_types;

  useEffect(() => {
    lastNameRef.current?.focus();
  }, [])

  useEffect(() => {
    if(isEditing) {
      if(focusField === "lastName") {
        lastNameRef.current?.focus();
      } else if(focusField === "email") {
        emailRef.current?.focus();
      }
    }
  }, [isEditing, focusField])

  useEffect(() => {
    if (selectedTalent && selectedTalent.id) {
      setErrors({ first_name: "", last_name: "", email: "", type: "", identity_verification_label: "", identity_verification_text: "" });
      setFormData({
          last_name: selectedTalent.last_name,
          first_name: selectedTalent.first_name,
          email: selectedTalent.email,
          talent_type: getOptionByValue(selectedTalent.talent_type, talentTypeOptions),
          referral_hiring: selectedTalent.referral_hiring,
          note: selectedTalent.note ? selectedTalent.note : "",
          identity_verification_label: selectedTalent.identity_verification_label ? selectedTalent.identity_verification_label : "",
          identity_verification_text: selectedTalent.identity_verification_text ? selectedTalent.identity_verification_text : ""
        });
      dispatch(setEditingTalent(false));
    } else {
      setFormData(initialData)
    }
  }, [selectedTalent]);

  const showToastCustom = (title: any, message: any, variant: any) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast title={title} message={message} variant={variant}
          snackbarKey={key} closeSnackbar={closeSnackbar} />
      ),
    });
  };

  const formatDateForDB = (value: any) => {
    if(value && value != 'null') {
      return (value + '').replaceAll("/", "-");
    }
    return null;
  };

  const getOptionByValue = (value: any, items: any) => {
    if (!value) {
      return null
    }
    for (const item of items) {
      if (item.id == value) {
        return item.id
      }
    }
    return null
  };

  const getNameByValue = (value: any, items: any) => {
    if (!value || !Array.isArray(items)) return null;
    return items.find((item: any) => item.id == value)?.name ?? '';
  };

  const getValueByObject = (object: any) => {
    if (!object) {
      return null;
    }
    return object.id;
  };

  const validateFormData = () => {
    let formErrors = {
      first_name: "",
      last_name: "",
      email: "",
      type: "",
      identity_verification_label: "",
      identity_verification_text: ""
    };

    const validateLastnameRes = validateFieldReq(formData.last_name, "姓");
    if(validateLastnameRes != true) {
      formErrors.last_name = String(validateLastnameRes);
    }

    const validateFirstnameRes = validateFieldReq(formData.first_name, "名");
    if(validateFirstnameRes != true) {
      formErrors.first_name = String(validateFirstnameRes);
    }

    // identity_verification_label and identity_verification_text are dependent on each other, so if one is filled, the other must be filled as well
    if (formData.identity_verification_label && !formData.identity_verification_text) {
      formErrors.identity_verification_text = "本人確認テキストを入力してください。";
    } else if (!formData.identity_verification_label && formData.identity_verification_text) {
      formErrors.identity_verification_label = "本人確認テキストラベルを入力してください。";
    }  

    const validateEmailRes = validateEmail(formData.email);
    if(validateEmailRes != true) {
      formErrors.email = String(validateEmailRes);
    }

    setErrors(formErrors);
    return !Object.values(formErrors).some((error) => error);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (isLoadingAddUpdate) { return; }

    // Validate form
    if (!validateFormData()) {
      return;
    }
    dispatch(setIsLoadingAddUpdate(true))

    if (selectedTalent && selectedTalent.id) {
      dispatch(updateTalent({ id: selectedTalent.id, requestBody: formData }));
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatReferrer = (talent: any) => {
    return formatPadStartNumber(talent?.id, 6) + " " + formatName(talent?.first_name, talent?.last_name)
  };

  const changeIsReferral = () => {
    return
  };

  const handleDownloadFileAttachment = async (url: string, filename?: string) => {
    try {
      await downloadFileAttachment(url, filename);
    } catch (error) {
      showToastCustom("メッセージ", i18n.t('MSG_GLOBAL_FILE_NOT_EXIST_OR_DELETED'), "error");
    }
  };

  return (
    <div className="w-[392px] bg-white rounded-2xl pt-3 alu-panel-box-shadow h-full flex flex-col pb-4">
      <div className="flex items-center justify-between px-4">
        <h2 className="text-xl leading-[1.875rem] font-bold alu-text-base-2">
          {!selectedTalent?.id
            ? "タレントを追加"
            : `${formatName(selectedTalent?.first_name, selectedTalent?.last_name)} さんの情報`}
        </h2>
        <CustomIconButton onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_8574_265)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_8574_265">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </CustomIconButton>
      </div>
      <div className="overflow-y-auto custom-scrollbar h-[calc(100%-72px)] px-4">
        <form onSubmit={handleSubmit} className="mt-6 w-full flex flex-col">
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2" htmlFor="fullName">
              氏名
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {formatName(selectedTalent?.first_name, selectedTalent?.last_name)}
            </p>
          </div>
          <div className="mb-6 pb-6 border-b alu-border">
            <label className="block mb-1 text-sm font-bold alu-text-base-2 pointer-events-none">
              メールアドレス {(selectedTalent.is_active && staffInfo?.type != STAFF_TYPE.GENERAL) && <span className="alu-text-error">*</span>} 
            </label>
            <TextInput
              size="md"
              error={errors.email}
              errorText={errors.email}
              type="text"
              ref={emailRef}
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="sample@sample.com"
              maxLength={254}
              disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || selectedTalent?.status == TALENT_TEMP_STATUS.APPROVING}
            />
          </div>

          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              登録リンクコード
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.register_code || 'ー'}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              種別
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.talent_type ? getNameByValue(selectedTalent?.talent_type, talentTypeOptions) : 'ー'}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              タグ
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {Array.isArray(selectedTalent?.tags) && selectedTalent?.tags.length > 0 ? (
                [...selectedTalent.tags]
                  .sort((a, b) => (a || '').localeCompare(b || ''))
                  .map((tag, index) => (
                    <div
                      key={index}
                      className="px-2 py-[0.125rem] text-sm alu-text-base-2 text-center rounded-sm bg-[#F7F7F7]"
                    >
                      {tag}
                    </div>
                  ))
              ) : (
                'ー'
              )}
            </div>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2" htmlFor="remarks">
              登録申請時メモ
            </label>
            <CustomTextarea
              onChange={handleChange}
              value={formData.note}
              rows={2}
              maxLength={2000}
              id="note"
              name="note"
              placeholder="入力してください"
              disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || selectedTalent?.status == TALENT_TEMP_STATUS.APPROVING}
            />
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2 pointer-events-none">
              本人確認用テキストラベル
            </label>
            <TextInput
              size="md"
              error={errors.identity_verification_label}
              errorText={errors.identity_verification_label}
              type="text"
              id="identity_verification_label"
              name="identity_verification_label"
              value={formData.identity_verification_label}
              onChange={handleChange}
              maxLength={100}
              disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || [TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVING].includes(selectedTalent?.status) || selectedTalent?.register_channel == 'web'}
            />
          </div>
          <div className="mb-6 pb-6 border-b alu-border">
            <label className="block mb-1 text-sm font-bold alu-text-base-2 pointer-events-none">
              本人確認用テキスト
            </label>
            <TextInput
              size="md"
              error={errors.identity_verification_text}
              errorText={errors.identity_verification_text}
              type="text"
              id="identity_verification_text"
              name="identity_verification_text"
              value={formData.identity_verification_text}
              onChange={handleChange}
              maxLength={100}
              disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || [TALENT_TEMP_STATUS.REGISTERED, TALENT_TEMP_STATUS.APPROVING].includes(selectedTalent?.status) || selectedTalent?.register_channel == 'web'}
            />
          </div>

          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              最終部署・社名
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.talent_register?.department || 'ー'}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              在籍期間
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {formatJoinLeaveCompanyDate(selectedTalent?.talent_register?.join_company_year, selectedTalent?.talent_register?.join_company_month, selectedTalent?.talent_register?.leave_company_year, selectedTalent?.talent_register?.leave_company_month)}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              経験した職種
            </label>
            {!selectedTalent?.talent_register?.occupation_1?.name ? 
              <p className="text-sm alu-text-base-2">ー</p>
              :
              <>
                <p className="text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.occupation_1?.name ? selectedTalent?.talent_register?.occupation_1?.name : 'ー'}
                </p>
                <p className="text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.occupation_2?.name ? ' > ' + selectedTalent?.talent_register?.occupation_2?.name : 'ー'}
                </p>
                <p className="ml-[6px] text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.occupation_3?.name ? ' > ' + selectedTalent?.talent_register?.occupation_3?.name : 'ー'}
                </p>
              </>
            }
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              経験した業種
            </label>
            {!selectedTalent?.talent_register?.industry_1?.name ? 
              <p className="text-sm alu-text-base-2">ー</p>
              :
              <>
                <p className="text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.industry_1?.name ? selectedTalent?.talent_register?.industry_1?.name : 'ー'}
                </p>
                <p className="text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.industry_2?.name ? ' > ' + selectedTalent?.talent_register?.industry_2?.name : 'ー'}
                </p>
                <p className="ml-[6px] text-sm alu-text-base-2">
                  {selectedTalent?.talent_register?.industry_3?.name ? ' > ' + selectedTalent?.talent_register?.industry_3?.name : 'ー'}
                </p>
              </>
            }
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              年齢
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {getAgeByYearMonthDay(selectedTalent?.talent_register?.birth_year, selectedTalent?.talent_register?.birth_month, selectedTalent?.talent_register?.birth_day)}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              居住地
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.talent_register?.city || 'ー'}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              紹介者（自由入力）
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.referral_hiring || 'ー'}
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              紹介者（タレント）
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.talent_register?.referrer_talent?.id ? formatReferrer(selectedTalent?.talent_register?.referrer_talent) : 'ー'}
            </p>
          </div>
          <div className="mb-6 flex items-end gap-2">
            <label className="block text-sm alu-text-base-2">
              リファラル登録
            </label>
            <CheckboxButton
              id="isReferral"
              name="isReferral"
              value={selectedTalent?.referral_hiring || selectedTalent?.talent_register?.referrer_talent?.id ? true : false}
              onChange={changeIsReferral}
              readOnly={true}
            />
          </div>
          <div className="mb-6">
            <label className="block mb-1 text-sm font-bold alu-text-base-2">
              社員番号
            </label>
            <p className="text-sm font-normal alu-text-base-2">
              {selectedTalent?.talent_register?.employee_number || 'ー'}
            </p>
          </div>
          <div className="mb-10 flex flex-col gap-4">
            {["RESUME", "CV", "OTHER"].map((item) => {
              let file = null;
              const uploadedFiles = selectedTalent?.uploaded_files || [];
              if(uploadedFiles.length > 0) {
                file = uploadedFiles.find((file: any) => file?.type === item);
              }

              return (
                <div className="w-full min-w-0">
                  <p className="text-sm alu-text-base-2 font-bold mb-1">{item === "RESUME" ? "履歴書" : item === "CV" ? "職務経歴書" : "その他のファイル"}</p>
                  {file ? <FileItem file={file} onFileClick={() => handleDownloadFileAttachment(file?.file_path, file?.file_name)} displayBorder={false} mobileBorderOnly={true} /> : 'ー'}
                </div>
              )
            })}
          </div>
        </form>
      </div>
      <div className="flex items-center justify-between px-4 pt-4 gap-4">
        <CustomButton
          type="button"
          onClick={() => isEditing && selectedTalent?.id ? dispatch(setEditingTalent(false)) : onClose()}
          width="full"
          variant="outline"
          size="md"
          disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || selectedTalent?.status == TALENT_TEMP_STATUS.APPROVING}
        >
          キャンセル
        </CustomButton>
        <CustomButton
          type="button" onClick={handleSubmit} disabled={isLoadingAddUpdate || !selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL || selectedTalent?.status == TALENT_TEMP_STATUS.APPROVING} size="md" width="full">
          <p>{isLoadingAddUpdate ? "送信中..." : !selectedTalent?.id ? "追加" : "保存"}</p>
            {isLoadingAddUpdate && (
              <span><LoadingSpinner/></span>
            )}
        </CustomButton>
      </div>
    </div>
  );
};

export default TalentRegistrationPanel;
